
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const TrainingPage = () => {
  const [activeTab, setActiveTab] = useState('courses');

  const courses = [
    {
      id: 1,
      title: 'AI Fundamentals for Real Estate',
      description: 'Master the basics of AI technology and its applications in real estate.',
      duration: '4 hours',
      level: 'Beginner',
      modules: 8,
      price: 'Free',
      image: 'https://public.readdy.ai/ai/img_res/5246867f827b9c399f309ec0dbff8424.jpg',
      features: ['Video Lessons', 'Interactive Quizzes', 'Certificate', 'Lifetime Access']
    },
    {
      id: 2,
      title: 'Advanced Lead Generation with AI',
      description: 'Learn advanced techniques for generating and qualifying leads using AI tools.',
      duration: '6 hours',
      level: 'Intermediate',
      modules: 12,
      price: '$199',
      image: 'https://readdy.ai/api/search-image?query=professional%20real%20estate%20agent%20analyzing%20lead%20generation%20data%20on%20computer%2C%20modern%20office%20setup%2C%20business%20analytics%20dashboard&width=400&height=250&seq=course2&orientation=landscape',
      features: ['Live Sessions', 'Case Studies', 'Templates', 'Expert Support']
    },
    {
      id: 3,
      title: 'AI Staging Mastery',
      description: 'Become an expert in AI-powered property staging and virtual design.',
      duration: '5 hours',
      level: 'Intermediate',
      modules: 10,
      price: '$149',
      image: 'https://readdy.ai/api/search-image?query=luxury%20home%20interior%20with%20AI-generated%20furniture%20staging%2C%20before%20and%20after%20comparison%2C%20professional%20property%20photography&width=400&height=250&seq=course3&orientation=landscape',
      features: ['Hands-on Projects', 'Design Templates', 'Portfolio Review', 'Industry Tools']
    },
    {
      id: 4,
      title: 'Market Analytics & Prediction',
      description: 'Use AI to analyze market trends and predict property values.',
      duration: '7 hours',
      level: 'Advanced',
      modules: 15,
      price: '$299',
      image: 'https://readdy.ai/api/search-image?query=real%20estate%20market%20analytics%20dashboard%20with%20AI%20predictions%2C%20charts%20and%20graphs%2C%20professional%20data%20analysis%20interface&width=400&height=250&seq=course4&orientation=landscape',
      features: ['Advanced Analytics', 'Prediction Models', 'Market Reports', 'Expert Mentoring']
    }
  ];

  const certifications = [
    {
      id: 1,
      title: 'Certified AI Real Estate Professional',
      description: 'Comprehensive certification covering all aspects of AI in real estate.',
      duration: '20 hours',
      price: '$499',
      badge: 'https://readdy.ai/api/search-image?query=professional%20certification%20badge%20for%20AI%20real%20estate%20specialist%2C%20gold%20and%20blue%20design%2C%20official%20credential%20symbol&width=200&height=200&seq=cert1&orientation=squarish'
    },
    {
      id: 2,
      title: 'AI Lead Generation Specialist',
      description: 'Specialized certification in AI-powered lead generation techniques.',
      duration: '12 hours',
      price: '$299',
      badge: 'https://readdy.ai/api/search-image?query=lead%20generation%20specialist%20certification%20badge%2C%20silver%20and%20blue%20design%2C%20professional%20credential%20symbol&width=200&height=200&seq=cert2&orientation=squarish'
    },
    {
      id: 3,
      title: 'AI Staging Expert',
      description: 'Expert-level certification in AI staging and virtual design.',
      duration: '15 hours',
      price: '$399',
      badge: 'https://readdy.ai/api/search-image?query=AI%20staging%20expert%20certification%20badge%2C%20bronze%20and%20blue%20design%2C%20professional%20credential%20symbol&width=200&height=200&seq=cert3&orientation=squarish'
    }
  ];

  const webinars = [
    {
      id: 1,
      title: 'The Future of Real Estate: AI Trends 2024',
      date: 'December 15, 2024',
      time: '2:00 PM EST',
      speaker: 'Dr. Sarah Chen, AI Research Director',
      attendees: 1250,
      status: 'upcoming'
    },
    {
      id: 2,
      title: 'Maximizing ROI with AI Tools',
      date: 'December 8, 2024',
      time: '1:00 PM EST',
      speaker: 'Mike Johnson, Top Real Estate Agent',
      attendees: 890,
      status: 'recorded'
    },
    {
      id: 3,
      title: 'AI Chatbots: Best Practices',
      date: 'December 1, 2024',
      time: '3:00 PM EST',
      speaker: 'Jennifer Lee, AI Specialist',
      attendees: 675,
      status: 'recorded'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              AI Training Center
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Master AI tools and techniques to transform your real estate business
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <div className="text-3xl font-bold">50+</div>
                <div className="text-blue-100">Courses Available</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <div className="text-3xl font-bold">10,000+</div>
                <div className="text-blue-100">Students Trained</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <div className="text-3xl font-bold">95%</div>
                <div className="text-blue-100">Success Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="flex justify-center">
            <div className="bg-white rounded-full p-2 shadow-lg">
              <div className="flex space-x-2">
                {[
                  { id: 'courses', label: 'Courses', icon: 'ri-book-open-line' },
                  { id: 'certifications', label: 'Certifications', icon: 'ri-award-line' },
                  { id: 'webinars', label: 'Webinars', icon: 'ri-live-line' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <i className={tab.icon}></i>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Tab */}
      {activeTab === 'courses' && (
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-gray-800">Professional Courses</h2>
              <p className="text-xl text-gray-600">Learn from industry experts and advance your career</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
              {courses.map((course) => (
                <div key={course.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <img 
                    src={course.image} 
                    alt={course.title}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        course.level === 'Beginner' ? 'bg-green-100 text-green-600' :
                        course.level === 'Intermediate' ? 'bg-yellow-100 text-yellow-600' :
                        'bg-red-100 text-red-600'
                      }`}>
                        {course.level}
                      </span>
                      <span className="text-2xl font-bold text-blue-600">{course.price}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-800">{course.title}</h3>
                    <p className="text-gray-600 mb-4">{course.description}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span><i className="ri-time-line mr-1"></i>{course.duration}</span>
                      <span><i className="ri-book-line mr-1"></i>{course.modules} modules</span>
                    </div>
                    <div className="space-y-2 mb-6">
                      {course.features.map((feature, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600">
                          <i className="ri-check-line text-green-500 mr-2"></i>
                          {feature}
                        </div>
                      ))}
                    </div>
                    <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                      Enroll Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Certifications Tab */}
      {activeTab === 'certifications' && (
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-gray-800">Professional Certifications</h2>
              <p className="text-xl text-gray-600">Validate your expertise with industry-recognized credentials</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {certifications.map((cert) => (
                <div key={cert.id} className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
                  <img 
                    src={cert.badge} 
                    alt={cert.title}
                    className="w-24 h-24 mx-auto mb-6 object-cover rounded-full"
                  />
                  <h3 className="text-xl font-bold mb-3 text-gray-800">{cert.title}</h3>
                  <p className="text-gray-600 mb-4">{cert.description}</p>
                  <div className="flex items-center justify-center text-sm text-gray-500 mb-6">
                    <i className="ri-time-line mr-1"></i>
                    {cert.duration}
                  </div>
                  <div className="text-2xl font-bold text-blue-600 mb-6">{cert.price}</div>
                  <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                    Get Certified
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Webinars Tab */}
      {activeTab === 'webinars' && (
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-gray-800">Live Webinars & Recordings</h2>
              <p className="text-xl text-gray-600">Join live sessions or watch recorded presentations</p>
            </div>
            <div className="space-y-6">
              {webinars.map((webinar) => (
                <div key={webinar.id} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                  <div className="grid md:grid-cols-4 gap-6 items-center">
                    <div className="md:col-span-2">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          webinar.status === 'upcoming' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          {webinar.status === 'upcoming' ? 'Upcoming' : 'Recorded'}
                        </span>
                        <span className="text-gray-500 text-sm">
                          <i className="ri-group-line mr-1"></i>
                          {webinar.attendees} attendees
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-2 text-gray-800">{webinar.title}</h3>
                      <p className="text-gray-600 mb-3">Speaker: {webinar.speaker}</p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-800">{webinar.date}</div>
                      <div className="text-gray-600">{webinar.time}</div>
                    </div>
                    <div className="text-center">
                      <button className={`px-6 py-3 rounded-lg font-bold transition-colors whitespace-nowrap cursor-pointer ${
                        webinar.status === 'upcoming' 
                          ? 'bg-green-600 text-white hover:bg-green-700' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}>
                        {webinar.status === 'upcoming' ? 'Register' : 'Watch Now'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Start Your AI Journey Today</h2>
          <p className="text-xl mb-8 text-blue-100">
            Join thousands of real estate professionals who have transformed their careers with AI
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Browse All Courses
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrainingPage;
